import { z } from "zod";
import { countryCode, currencyCode, hexColor, optionalEmail, optionalText, phoneInput, text, timeZone } from "./common";

/*
 * The jsonb sections of org.tenant_settings. Every field has a default, so a section stored as {}
 * (or written by an older version) always parses into a complete, valid object. Bump
 * SETTINGS_VERSION and add a migration step in code when a field changes meaning.
 */
export const SETTINGS_VERSION = 1;

export const patientSettingsSchema = z.object({
  codePrefix: z.string().regex(/^[A-Z]{0,6}-?$/).default("P-"),
  codePadding: z.number().int().min(3).max(10).default(6),
  requirePhone: z.boolean().default(false),
  duplicateCheck: z.boolean().default(true),
});

export const appointmentSettingsSchema = z.object({
  slotIntervalMinutes: z.number().int().min(5).max(60).default(15),
  defaultDurationMinutes: z.number().int().min(5).max(480).default(30),
  minBookingNoticeMinutes: z.number().int().min(0).max(10_080).default(0),
  maxAdvanceBookingDays: z.number().int().min(1).max(730).default(180),
  requireChair: z.boolean().default(false),
  requireRoom: z.boolean().default(false),
  bufferMinutes: z.number().int().min(0).max(120).default(0),
  /**
   * Public booking page: off; "requests" (the patient asks for a time, staff confirm); "instant" (booked at once
   * after the patient confirms a one-time code; falls back to requests while no code can be delivered).
   */
  onlineBooking: z.enum(["off", "requests", "instant"]).default("off"),
});

export const TAX_SCHEMES = ["none", "in_gst", "vat", "sales_tax", "other"] as const;
export type TaxScheme = (typeof TAX_SCHEMES)[number];

const docPrefix = z.string().regex(/^[A-Z0-9/-]{0,12}$/, "Capital letters, digits, - and / only (max 12)");

export const invoiceSettingsSchema = z.object({
  invoicePrefix: docPrefix.default("INV-"),
  receiptPrefix: docPrefix.default("RCP-"),
  creditNotePrefix: docPrefix.default("CN-"),
  refundPrefix: docPrefix.default("RF-"),
  footerNote: z.string().max(1000).default(""),
  maxDiscountPercentWithoutApproval: z.number().min(0).max(100).default(10),
  /**
   * How tax is labelled and split on invoices. "in_gst": CGST + SGST within the branch's state, IGST
   * across states, GSTIN and state code required. Which services are taxed (and at what rate) is
   * always the service's tax category; the clinic's CA confirms that set-up (docs/DECISIONS.md D7).
   */
  taxScheme: z.enum(TAX_SCHEMES).default("none"),
  /** Days from issue to due date; 0 = due on issue. */
  defaultDueDays: z.number().int().min(0).max(365).default(0),
});

export const notificationSettingsSchema = z.object({
  /** Superseded by reminder policies (Phase 5); kept so stored settings still parse. */
  reminderOffsetsMinutes: z.array(z.number().int().min(15).max(43_200)).max(5).default([1440, 120]),
  /** Link used by {{review_link}} (e.g. the clinic's Google review page). */
  reviewLink: z.union([z.literal(""), z.url({ protocol: /^https$/ }).max(500)]).default(""),
  quietHoursStart: z.string().regex(/^\d{2}:\d{2}$/).default("21:00"),
  quietHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).default("08:00"),
});

export const brandingSchema = z.object({
  primaryColor: hexColor.default("#0F766E"),
  accentColor: hexColor.default("#F59E0B"),
});

export type PatientSettings = z.infer<typeof patientSettingsSchema>;
export type AppointmentSettings = z.infer<typeof appointmentSettingsSchema>;
export type InvoiceSettings = z.infer<typeof invoiceSettingsSchema>;
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;
export type Branding = z.infer<typeof brandingSchema>;

export const clinicProfileInput = z.object({
  displayName: text(200),
  legalName: optionalText(200),
  email: optionalEmail,
  phone: phoneInput,
  website: z
    .string()
    .trim()
    .max(300)
    .nullish()
    .transform((v) => (v ? v : null))
    .pipe(z.url({ protocol: /^https?$/ }).nullable()),
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  city: optionalText(100),
  region: optionalText(100),
  postalCode: optionalText(20),
  countryCode,
  timeZone,
  currencyCode,
  locale: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/),
  dateFormat: z.enum(["dd/MM/yyyy", "MM/dd/yyyy", "yyyy-MM-dd"]),
  timeFormat: z.enum(["h:mm a", "HH:mm"]),
  taxRegistrationLabel: optionalText(40),
  taxRegistrationNumber: optionalText(40),
});

export const updateSettingsInput = z.object({
  version: z.number().int().min(1),
  profile: clinicProfileInput.optional(),
  patient: patientSettingsSchema.optional(),
  appointment: appointmentSettingsSchema.optional(),
  invoice: invoiceSettingsSchema.optional(),
  notification: notificationSettingsSchema.optional(),
  branding: brandingSchema.optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsInput>;

export interface ClinicSettings {
  profile: z.infer<typeof clinicProfileInput>;
  patient: PatientSettings;
  appointment: AppointmentSettings;
  invoice: InvoiceSettings;
  notification: NotificationSettings;
  branding: Branding;
  version: number;
  updatedAt: string;
}
